import { strToU8, zipSync } from 'fflate';
import type { ReportDataWarning, ReportGeneration } from './generated-report';

export type CaseInflowSourceFile = {
  id: string;
  name: string;
  path: string;
  modifiedTime: string | null;
  webViewLink: string | null;
  year: number;
  month: number | null;
  referenceDate?: string | null;
};

export type CaseInflowRow = {
  year: number;
  month: number;
  label: string;
  incoming: number;
  closed: number;
  mailFallback: boolean;
  mailIncomplete: boolean;
  mailReferenceDate: string | null;
  components: {
    incoming: {
      currentMasterAllOpened: number;
      mailNonWebFirst12: number;
      masterNonWebFirst12Fallback: number;
      currentMasterNonWebFrom13: number;
    };
    closed: {
      currentMasterClosedOpened: number;
      mailNonWebFirst12: number;
      masterNonWebFirst12Fallback: number;
      currentMasterNonWebFrom13: number;
      previousMastersClosed: number;
    };
  };
};

export type CaseInflowAnalysis = {
  selectedYear: number;
  selectedMonth: number;
  cutoffDay: number;
  rows: CaseInflowRow[];
  totals: { incoming: number; closed: number };
  masterFiles: CaseInflowSourceFile[];
  mailFiles: CaseInflowSourceFile[];
  mailFallbackYears: number[];
  mailFallbackPeriods: string[];
  mailIncompletePeriods: Array<{ label: string; referenceDate: string }>;
  dataWarnings?: ReportDataWarning[];
  generation?: ReportGeneration;
};

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function inlineCell(reference: string, value: string, style: number): string {
  return `<c r="${reference}" t="inlineStr" s="${style}"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function numberCell(reference: string, value: number): string {
  return `<c r="${reference}" s="3"><v>${Number.isFinite(value) ? value : 0}</v></c>`;
}

function workbookFiles(analysis: CaseInflowAnalysis): Record<string, Uint8Array> {
  const lastRow = analysis.rows.length + 1;
  const headerCells = [
    inlineCell('A1', 'ANDAMENTO ANNUALE', 1),
    inlineCell('B1', 'Entrata', 1),
    inlineCell('C1', 'Chiusi', 1),
  ].join('');
  const dataRows = analysis.rows.map((row, index) => {
    const excelRow = index + 2;
    return `<row r="${excelRow}" ht="24" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.label, 2),
      numberCell(`B${excelRow}`, row.incoming),
      numberCell(`C${excelRow}`, row.closed),
    ].join('')}</row>`;
  }).join('');
  const now = new Date().toISOString();

  return {
    '[Content_Types].xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`),
    '_rels/.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`),
    'docProps/core.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>Case in entrata ${analysis.selectedYear}</dc:title>
  <dc:creator>Talos</dc:creator>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`),
    'docProps/app.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Talos</Application></Properties>`),
    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <bookViews><workbookView xWindow="0" yWindow="0" windowWidth="18000" windowHeight="11000"/></bookViews>
  <sheets><sheet name="Case in entrata" sheetId="1" r:id="rId1"/></sheets>
  <calcPr calcId="191029" fullCalcOnLoad="1"/>
</workbook>`),
    'xl/_rels/workbook.xml.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    'xl/styles.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="3">
    <font><sz val="11"/><color rgb="FF263C4D"/><name val="Aptos"/><family val="2"/></font>
    <font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Aptos Display"/><family val="2"/></font>
    <font><b/><sz val="11"/><color rgb="FF173249"/><name val="Aptos"/><family val="2"/></font>
  </fonts>
  <fills count="4">
    <fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF173249"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF1F5F8"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border><left style="thin"><color rgb="FFDDE5EB"/></left><right style="thin"><color rgb="FFDDE5EB"/></right><top style="thin"><color rgb="FFDDE5EB"/></top><bottom style="thin"><color rgb="FFDDE5EB"/></bottom><diagonal/></border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="4">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="2" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment vertical="center"/></xf>
    <xf numFmtId="3" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`),
    'xl/worksheets/sheet1.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:C${lastRow}"/>
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="20"/>
  <cols><col min="1" max="1" width="24" customWidth="1"/><col min="2" max="3" width="16" customWidth="1"/></cols>
  <sheetData><row r="1" ht="42" customHeight="1">${headerCells}</row>${dataRows}</sheetData>
  <autoFilter ref="A1:C${lastRow}"/>
  <pageMargins left="0.3" right="0.3" top="0.5" bottom="0.5" header="0.2" footer="0.2"/>
</worksheet>`),
  };
}

export function createCaseInflowWorkbook(analysis: CaseInflowAnalysis): Uint8Array {
  return zipSync(workbookFiles(analysis), { level: 6 });
}

export function caseInflowWorkbookFileName(year: number, month: number): string {
  return `case-in-entrata-${year}-${String(month).padStart(2, '0')}.xlsx`;
}
